import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatApiError } from "../lib/api";
import { supabase } from "../lib/supabase";
import NutrientCard from "../components/NutrientCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Flame, Zap, Fish, Leaf, Download } from "lucide-react";
import { exportDailyPdf } from "../lib/pdf";
import { localizeFoodName, localizeServing } from "../lib/foodTranslations";

const DIALYSIS_TIPS = {
  en: [
    "Check food labels for sodium before choosing packaged foods.",
    "Ask your renal dietitian how often you should include high-potassium foods.",
    "Phosphorus additives are common in processed foods. Look for ingredients with “phos”.",
    "Keeping a food log can help your care team understand your daily patterns.",
    "Serving size matters. A small portion can change the nutrient total.",
    "Try to plan meals before you are very hungry to avoid quick high-sodium choices.",
    "Bring your food log to your dietitian appointment when possible.",
    "Protein needs can be different for dialysis patients. Follow your care team’s target.",
    "Restaurant meals can be high in sodium. Ask for sauces or dressings on the side.",
    "If a food surprises you in the tracker, save it as a question for your dietitian.",
  ],
  ar: [
    "تحقّق من كمية الصوديوم على ملصقات الأطعمة المعلّبة.",
    "اسأل اختصاصي التغذية الكلوية عن الأطعمة العالية بالبوتاسيوم.",
    "قد تحتوي الأطعمة المصنّعة على إضافات فوسفور. ابحث عن مكونات تحتوي على phos.",
    "تسجيل الطعام يساعد فريقك الطبي على فهم نمطك اليومي.",
    "حجم الحصة مهم وقد يغيّر مجموع العناصر الغذائية.",
    "حاول التخطيط للوجبات قبل الشعور بالجوع الشديد لتجنب الخيارات العالية بالصوديوم.",
    "أحضر سجل الطعام إلى موعد اختصاصي التغذية عند الإمكان.",
    "احتياجات البروتين قد تختلف لمرضى غسيل الكلى. اتبع هدف فريقك الطبي.",
    "وجبات المطاعم قد تكون عالية بالصوديوم. اطلب الصلصات جانبًا.",
    "إذا فاجأك طعام في المتتبع، احفظه كسؤال لاختصاصي التغذية.",
  ],
};

const DAILY_QUOTES = {
  en: [
    "Small choices today can protect your energy tomorrow.",
    "Consistency matters more than perfection.",
    "Track first, adjust with your healthcare team.",
    "A clear food log makes better conversations with your care team.",
    "Progress is built one meal at a time.",
  ],
  ar: [
    "الاستمرارية أهم من الكمال.",
    "تسجيل الطعام بوضوح يساعدك في الحديث مع فريقك الطبي.",
    "كل وجبة صغيرة خطوة نحو متابعة أفضل.",
    "اختيارات بسيطة اليوم قد تساعدك غدًا.",
  ],
};

const LIMITS = { potassium: 3000, phosphorus: 1000, sodium: 2000, protein: 60 };
const today = () => new Date().toISOString().slice(0, 10);
const localDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const logDate = (log) => (log.logged_at || new Date().toISOString()).slice(0, 10);
const isMedicationActive = (medication, now = Date.now()) => {
  const durationDays = Number(medication.duration_days || 0);

  if (!durationDays) return true;

  const createdAt = medication.created_at || medication.createdAt;
  if (!createdAt) return true;

  const createdTime = new Date(createdAt).getTime();
  if (!Number.isFinite(createdTime)) return true;

  return now < createdTime + durationDays * 24 * 60 * 60 * 1000;
};
const readGuestFoodLogs = () => {
  if (typeof window === "undefined") return [];

  try {
    const savedLogs = localStorage.getItem("guestFoodLogs");
    const parsedLogs = savedLogs ? JSON.parse(savedLogs) : [];
    return Array.isArray(parsedLogs) ? parsedLogs : [];
  } catch {
    localStorage.removeItem("guestFoodLogs");
    return [];
  }
};
const mapEntry = (log) => {
  const food = log.foods || {};
  const quantity = Number(log.quantity || 1);
  return {
    id: log.id,
    quantity,
    food_name: log.food_name || food.name || "Unknown food",
    category: log.category || food.category || "",
    serving_size: log.serving_size || food.serving_size || "",
    potassium: Math.round(Number(log.potassium_mg || 0)),
    phosphorus: Math.round(Number(log.phosphorus_mg || 0)),
    sodium: Math.round(Number(log.sodium_mg || 0)),
    protein: Math.round(Number(log.protein_g || 0)),
  };
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const guestMode =
    typeof window !== "undefined" &&
    localStorage.getItem("guestMode") === "true";
  const medicationStorageOwnerId = guestMode ? "guest" : user?.id;
  const [summary, setSummary] = useState(null);
  const [profile, setProfile] = useState({
    full_name: "",
    weight_kg: 70,
  });
  const [foods, setFoods] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedFoodId, setSelectedFoodId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [tipIndex, setTipIndex] = useState(new Date().getDate());
  const [medications, setMedications] = useState([]);
  const [medicationHistory, setMedicationHistory] = useState({});
  const [medicationClock, setMedicationClock] = useState(Date.now());
  const [medForm, setMedForm] = useState({
    name: "",
    times: "08:00",
    durationDays: "",
    note: "",
  });

  const loadProfile = useCallback(async () => {
    if (guestMode) {
      try {
        const savedProfile = localStorage.getItem("guestProfile");
        const guestProfile = savedProfile ? JSON.parse(savedProfile) : null;

        if (guestProfile) {
          setProfile({
            full_name: guestProfile.full_name || "",
            weight_kg: guestProfile.weight_kg || 70,
          });
        }
      } catch {
        localStorage.removeItem("guestProfile");
      }

      return;
    }

    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, weight_kg")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProfile({
          full_name: data.full_name || "",
          weight_kg: data.weight_kg || 70,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [guestMode, user?.id]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const load = useCallback(async () => {
    try {
      if (guestMode) {
        const logs = readGuestFoodLogs();
        const entries = logs.filter((log) => logDate(log) === today()).map(mapEntry);
        const totals = entries.reduce((acc, entry) => ({
          potassium: acc.potassium + entry.potassium,
          phosphorus: acc.phosphorus + entry.phosphorus,
          sodium: acc.sodium + entry.sodium,
          protein: acc.protein + entry.protein,
        }), { potassium: 0, phosphorus: 0, sodium: 0, protein: 0 });
        const foodsRes = await supabase.from("foods").select("*");
        if (foodsRes.error) throw foodsRes.error;

        setSummary({ date: today(), limits: LIMITS, totals, entries });
        setFoods(foodsRes.data || []);
        return;
      }

      if (!user?.id) return;
      const [logsRes, foodsRes] = await Promise.all([
        supabase
          .from("food_logs")
          .select("id, food_id, food_name, category, serving_size, quantity, potassium_mg, phosphorus_mg, sodium_mg, protein_g, logged_at, foods(name, category, serving_size)")
          .eq("user_id", user.id),
        supabase.from("foods").select("*"),
      ]);
      const error = logsRes.error || foodsRes.error;
      if (error) throw error;
      const entries = (logsRes.data || []).filter((log) => logDate(log) === today()).map(mapEntry);
      const totals = entries.reduce((acc, entry) => ({
        potassium: acc.potassium + entry.potassium,
        phosphorus: acc.phosphorus + entry.phosphorus,
        sodium: acc.sodium + entry.sodium,
        protein: acc.protein + entry.protein,
      }), { potassium: 0, phosphorus: 0, sodium: 0, protein: 0 });
      setSummary({ date: today(), limits: LIMITS, totals, entries });
      setFoods(foodsRes.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [guestMode, user?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user?.id) return;
    const notesKey = `ddp-notes-${user.id}`;
    const savedNotes = localStorage.getItem(notesKey);

    if (savedNotes) {
      try {
        setNotes(JSON.parse(savedNotes));
      } catch {
        localStorage.removeItem(notesKey);
        setNotes([]);
      }
    } else {
      setNotes([]);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!medicationStorageOwnerId) {
      setMedications([]);
      setMedicationHistory({});
      return;
    }

    const medicationsKey = `ddp-medications-${medicationStorageOwnerId}`;
    const medicationHistoryKey = `ddp-medication-history-${medicationStorageOwnerId}`;
    const savedMedications = localStorage.getItem(medicationsKey);
    const savedHistory = localStorage.getItem(medicationHistoryKey);

    if (savedMedications) {
      try {
        const parsedMedications = JSON.parse(savedMedications);
        if (!Array.isArray(parsedMedications)) throw new Error("Invalid medications");
        setMedications(parsedMedications);
      } catch {
        localStorage.removeItem(medicationsKey);
        setMedications([]);
      }
    } else {
      setMedications([]);
    }

    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        if (!parsedHistory || typeof parsedHistory !== "object" || Array.isArray(parsedHistory)) {
          throw new Error("Invalid medication history");
        }
        setMedicationHistory(parsedHistory);
      } catch {
        localStorage.removeItem(medicationHistoryKey);
        setMedicationHistory({});
      }
    } else {
      setMedicationHistory({});
    }
  }, [medicationStorageOwnerId]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setMedicationClock(Date.now()), 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const addFood = async () => {
    if (!selectedFoodId) return toast.error(t("please_select_food"));
    const selectedFood = foods.find((food) => food.id === selectedFoodId);
    const amount = Number(quantity || 1);

    if (guestMode) {
      const nextLog = {
        id: crypto.randomUUID(),
        food_id: selectedFoodId,
        food_name: selectedFood?.name || "",
        category: selectedFood?.category || "",
        serving_size: selectedFood?.serving_size || "",
        quantity: amount,
        potassium_mg: Math.round(Number(selectedFood?.potassium_mg || 0) * amount),
        phosphorus_mg: Math.round(Number(selectedFood?.phosphorus_mg || 0) * amount),
        sodium_mg: Math.round(Number(selectedFood?.sodium_mg || 0) * amount),
        protein_g: Math.round(Number(selectedFood?.protein_g || 0) * amount),
        logged_at: new Date().toISOString(),
      };
      const nextLogs = [nextLog, ...readGuestFoodLogs()];

      localStorage.setItem("guestFoodLogs", JSON.stringify(nextLogs));
      toast.success(t("food_logged"));
      setAddOpen(false); setSelectedFoodId(""); setQuantity(1);
      load();
      return;
    }

    try {
      const { error } = await supabase
        .from("food_logs")
        .insert({
          user_id: user.id,
          food_id: selectedFoodId,
          food_name: selectedFood?.name || "",
          category: selectedFood?.category || "",
          serving_size: selectedFood?.serving_size || "",
          quantity: amount,
          potassium_mg: Math.round(Number(selectedFood?.potassium_mg || 0) * amount),
          phosphorus_mg: Math.round(Number(selectedFood?.phosphorus_mg || 0) * amount),
          sodium_mg: Math.round(Number(selectedFood?.sodium_mg || 0) * amount),
          protein_g: Math.round(Number(selectedFood?.protein_g || 0) * amount),
          logged_at: new Date().toISOString(),
        });
      if (error) throw error;
      toast.success(t("food_logged"));
      setAddOpen(false); setSelectedFoodId(""); setQuantity(1);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveNote = async () => {
    if (!newNote.trim()) {
      toast.error(t("note_required"));
      return;
    }

    const notesKey = `ddp-notes-${user.id}`;
    const nextNotes = [
      {
        id: crypto.randomUUID(),
        content: newNote.trim(),
        date: today(),
        createdAt: new Date().toISOString(),
      },
      ...notes,
    ];

    localStorage.setItem(notesKey, JSON.stringify(nextNotes));
    setNotes(nextNotes);
    setNewNote("");
    toast.success(t("note_saved"));
  };

  const deleteNote = async (id) => {
    const notesKey = `ddp-notes-${user.id}`;
    const nextNotes = notes.filter((note) => note.id !== id);

    localStorage.setItem(notesKey, JSON.stringify(nextNotes));
    setNotes(nextNotes);
    toast.success(t("note_deleted"));
  };

  const saveMedications = (nextMedications) => {
    if (!medicationStorageOwnerId) return;
    localStorage.setItem(`ddp-medications-${medicationStorageOwnerId}`, JSON.stringify(nextMedications));
    setMedications(nextMedications);
  };

  const saveMedicationHistory = (nextHistory) => {
    if (!medicationStorageOwnerId) return;
    localStorage.setItem(`ddp-medication-history-${medicationStorageOwnerId}`, JSON.stringify(nextHistory));
    setMedicationHistory(nextHistory);
  };

  const addMedication = () => {
    const name = medForm.name.trim();
    if (!name) {
      toast.error(t("medication_name_required"));
      return;
    }

    const times = medForm.times
      .split(",")
      .map((time) => time.trim())
      .filter(Boolean)
      .filter((time) => /^([01]\d|2[0-3]):[0-5]\d$/.test(time));

    if (!times.length) {
      toast.error(t("medication_valid_time_required"));
      return;
    }

    const durationValue = medForm.durationDays.trim();
    const durationDays = durationValue ? Number(durationValue) : null;

    if (durationValue && (!Number.isInteger(durationDays) || durationDays <= 0)) {
      toast.error("Duration must be a number greater than 0.");
      return;
    }

    const createdAt = new Date().toISOString();
    const nextMedication = {
      id: crypto.randomUUID(),
      name,
      times: [...new Set(times)],
      duration_days: durationDays,
      note: medForm.note.trim(),
      created_at: createdAt,
      createdAt,
    };

    saveMedications([...medications, nextMedication]);
    setMedForm({ name: "", times: "08:00", durationDays: "", note: "" });
    toast.success(t("medication_added"));
  };

  const deleteMedication = (id) => {
    const nextMedications = medications.filter((med) => med.id !== id);
    const nextHistory = Object.fromEntries(
      Object.entries(medicationHistory).map(([date, dayHistory]) => {
        const { [id]: _removed, ...rest } = dayHistory || {};
        return [date, rest];
      })
    );

    saveMedications(nextMedications);
    saveMedicationHistory(nextHistory);
    toast.success(t("medication_removed"));
  };

  const toggleDoseTaken = (medicationId, time) => {
    const date = localDateKey();
    const currentlyTaken = Boolean(medicationHistory[date]?.[medicationId]?.[time]);
    const nextHistory = {
      ...medicationHistory,
      [date]: {
        ...(medicationHistory[date] || {}),
        [medicationId]: {
          ...(medicationHistory[date]?.[medicationId] || {}),
          [time]: !currentlyTaken,
        },
      },
    };

    saveMedicationHistory(nextHistory);
    toast.success(currentlyTaken ? t("medication_dose_not_taken") : t("medication_dose_taken"));
  };

  const deleteLog = async (id) => {
    if (guestMode) {
      const nextLogs = readGuestFoodLogs().filter((log) => log.id !== id);

      localStorage.setItem("guestFoodLogs", JSON.stringify(nextLogs));
      load();
      return;
    }

    const { error } = await supabase.from("food_logs").delete().eq("id", id).eq("user_id", user.id);
    if (error) return toast.error(formatApiError(error));
    load();
  };

  if (!summary) return <div className="text-muted-foreground">{t("loading")}</div>;

  const limits = summary.limits;
  const totals = summary.totals;
  const tips = DIALYSIS_TIPS[lang] || DIALYSIS_TIPS.en;
  const quotes = DAILY_QUOTES[lang] || DAILY_QUOTES.en;
  const dailyQuote = quotes[new Date().getDate() % quotes.length];
  const dailyTip = tips[tipIndex % tips.length];
  const todayMedicationHistory = medicationHistory[localDateKey()] || {};
  const activeMedications = medications.filter((medication) => isMedicationActive(medication, medicationClock));
  const totalDosesToday = activeMedications.reduce((total, med) => total + med.times.length, 0);
  const takenDosesToday = activeMedications.reduce((total, med) => {
    return total + med.times.filter((time) => todayMedicationHistory[med.id]?.[time]).length;
  }, 0);
  const lastSevenMedicationDays = Array.from({ length: 7 }, (_, index) => {
    const date = new Date();
    date.setDate(date.getDate() - index);
    const key = localDateKey(date);
    const dayHistory = medicationHistory[key] || {};
    const total = totalDosesToday;
    const taken = activeMedications.reduce((count, med) => {
      return count + med.times.filter((time) => dayHistory[med.id]?.[time]).length;
    }, 0);

    return {
      key,
      label: date.toLocaleDateString(undefined, { month: "short", day: "2-digit" }),
      taken,
      total,
    };
  });

  const getSafetySummary = (totals, limits) => {
    const warnings = [];

    if (totals.potassium >= limits.potassium * 0.9) warnings.push(t("potassium_close_limit"));
    if (totals.phosphorus >= limits.phosphorus * 0.9) warnings.push(t("phosphorus_close_limit"));
    if (totals.sodium >= limits.sodium * 0.9) warnings.push(t("sodium_close_limit"));
    if (totals.protein >= limits.protein * 0.9) warnings.push(t("protein_close_target"));

    if (summary?.entries?.length === 0) {
      return t("no_foods_logged_today");
    }

    if (warnings.length === 0) {
      return t("today_safe_so_far");
    }

    return warnings[0];
  };

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
            {t("daily_overview")}
          </h1>
          <p className="text-muted-foreground mt-1">{summary.date} · {profile.full_name || "-"}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportDailyPdf(summary, profile.full_name || user?.name || "-", profile.weight_kg)} className="gap-2" data-testid="export-pdf-btn">
            <Download className="w-4 h-4" /> {t("export_pdf")}
          </Button>
          <Button variant="outline" onClick={() => window.print()} className="gap-2 hidden md:inline-flex" data-testid="print-btn">
            <Printer className="w-4 h-4" /> {t("print_page")}
          </Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2" data-testid="add-food-btn"><Plus className="w-4 h-4" /> {t("add_food")}</Button>
            </DialogTrigger>
            <DialogContent data-testid="add-food-dialog">
              <DialogHeader><DialogTitle>{t("add_food")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t("search_food")}</Label>
                  <Select value={selectedFoodId} onValueChange={setSelectedFoodId}>
                    <SelectTrigger className="h-12" data-testid="food-select"><SelectValue placeholder={t("search_food")} /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {foods.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {localizeFoodName(f.name, lang)} ({localizeServing(f.serving_size, lang)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label>{t("quantity")}</Label>
                    <Input type="number" step="0.5" min="0.5" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="h-12" data-testid="quantity-input" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddOpen(false)}>{t("cancel")}</Button>
                <Button className="btn-primary" onClick={addFood} data-testid="confirm-add-food">{t("add")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Nutrients */}
      <div>
        <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>{t("harmful_nutrients")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <NutrientCard nutrientKey="potassium" value={totals.potassium} limit={limits.potassium} unit="mg" icon={Flame} />
          <NutrientCard nutrientKey="phosphorus" value={totals.phosphorus} limit={limits.phosphorus} unit="mg" icon={Zap} />
          <NutrientCard nutrientKey="sodium" value={totals.sodium} limit={limits.sodium} unit="mg" icon={Leaf} />
          <NutrientCard nutrientKey="protein" value={totals.protein} limit={limits.protein} unit="g" icon={Fish} />
        </div>
      </div>

      {/* Support cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bento-card" data-testid="safety-summary-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t("today_safety_summary")}</div>
          <p className="text-base leading-relaxed font-medium">{getSafetySummary(totals, limits)}</p>
          <p className="mt-3 text-xs text-muted-foreground">{t("safety_summary_footer")}</p>
        </div>

        <div className="bento-card" data-testid="dialysis-tip-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t("dialysis_tip")}</div>
          <p className="text-base leading-relaxed">{dailyTip}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => setTipIndex((index) => (index + 1) % tips.length)}
            data-testid="next-tip-btn"
          >
            {t("show_another_tip")}
          </Button>
        </div>

        <div className="bento-card" data-testid="quote-card" style={{ background: "linear-gradient(135deg, var(--rs-sodium)15, transparent)" }}>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t("quote_of_day")}</div>
          <p className="text-lg leading-relaxed italic">&ldquo;{dailyQuote}&rdquo;</p>
        </div>
      </div>

      {/* Entries + Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bento-card" data-testid="entries-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">{t("entries_today")}</h3>
            <span className="text-sm text-muted-foreground">{summary.entries.length}</span>
          </div>
          {summary.entries.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">{t("no_entries")}</div>
          ) : (
            <div className="space-y-2">
              {summary.entries.map((e) => (
                <div key={e.id} className="flex items-center justify-between p-3 rounded-xl bg-muted/40 border border-border" data-testid={`log-${e.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{localizeFoodName(e.food_name, lang)} <span className="text-muted-foreground font-normal">× {e.quantity}</span></div>
                    {e.serving_size && (
                      <div className="text-xs text-muted-foreground">{localizeServing(e.serving_size, lang)}</div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      K {e.potassium}mg · P {e.phosphorus}mg · Na {e.sodium}mg · Pr {e.protein}g
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => deleteLog(e.id)} aria-label={t("delete")} data-testid={`delete-log-${e.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bento-card" data-testid="medication-reminder-card">
            <div className="mb-4">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-base font-semibold leading-tight">{t("medication_reminder")}</h3>
                <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                  {t("today")} {takenDosesToday}/{totalDosesToday}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{t("medication_helper")}</p>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="medication-name">{t("medication_name")}</Label>
                 <Input
                   id="medication-name"
                   value={medForm.name}
                  onChange={(e) => setMedForm((current) => ({ ...current, name: e.target.value }))}
                  className="h-11 mt-1"
                   data-testid="medication-name-input"
                 />
                 <p className="mt-1 text-xs font-light text-muted-foreground/80">{t("medication_name_helper")}</p>
               </div>
               <div>
                 <Label htmlFor="medication-times">{t("medication_times")}</Label>
                <Input
                  id="medication-times"
                  value={medForm.times}
                  onChange={(e) => setMedForm((current) => ({ ...current, times: e.target.value }))}
                  placeholder="08:00, 14:00, 21:00"
                  className="h-11 mt-1"
                   data-testid="medication-times-input"
                 />
                 <p className="mt-1 text-xs font-light text-muted-foreground/80">{t("medication_times_helper")}</p>
               </div>
               <div>
                 <Label htmlFor="medication-duration">{t("medication_duration")}</Label>
                 <Input
                  id="medication-duration"
                  type="number"
                  min="1"
                  step="1"
                  value={medForm.durationDays}
                  onChange={(e) => setMedForm((current) => ({ ...current, durationDays: e.target.value }))}
                  className="h-11 mt-1"
                   data-testid="medication-duration-input"
                 />
                 <p className="mt-1 text-xs font-light text-muted-foreground/80">{t("medication_duration_helper")}</p>
               </div>
              <div>
                <Label htmlFor="medication-note">{t("note")}</Label>
                <Textarea
                  id="medication-note"
                  value={medForm.note}
                  onChange={(e) => setMedForm((current) => ({ ...current, note: e.target.value }))}
                  rows={2}
                  data-testid="medication-note-input"
                 />
               </div>
             </div>

             <Button className="btn-primary mt-4 w-full" onClick={addMedication} data-testid="add-medication-btn">
              {t("add_medication")}
            </Button>

            <div className="mt-4 space-y-3">
              {activeMedications.length === 0 ? (
                <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                  {t("medication_empty")}
                </div>
              ) : (
                activeMedications.map((med) => (
                  <div key={med.id} className="rounded-lg border border-border bg-muted/30 p-3 text-sm" data-testid={`medication-${med.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold">{med.name}</div>
                        {med.note && <div className="mt-1 text-muted-foreground">{med.note}</div>}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => deleteMedication(med.id)}
                        data-testid={`delete-medication-${med.id}`}
                      >
                        {t("delete")}
                      </Button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {med.times.map((time) => {
                        const taken = Boolean(todayMedicationHistory[med.id]?.[time]);
                        return (
                          <Button
                            key={time}
                            variant={taken ? "default" : "outline"}
                            size="sm"
                            onClick={() => toggleDoseTaken(med.id, time)}
                            data-testid={`dose-${med.id}-${time}`}
                          >
                            {time} {taken ? t("taken") : t("pending")}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-5 border-t border-border pt-4">
              <h4 className="text-sm font-semibold">{t("medication_last_7_days")}</h4>
              {activeMedications.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">{t("medication_add_to_track")}</p>
              ) : (
                <div className="mt-2 space-y-1.5">
                  {lastSevenMedicationDays.map((day) => (
                    <div key={day.key} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{day.label}</span>
                      <span className="font-medium">{day.taken} / {day.total} {t("taken")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">{t("medication_disclaimer")}</p>
          </div>

          <div className="bento-card" data-testid="notes-card">
            <div className="mb-4">
              <h3 className="font-semibold text-lg">{t("notes")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t("notes_helper")}</p>
            </div>
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder={t("add_note")}
              rows={3}
              data-testid="note-input"
            />
            <Button className="btn-primary mt-3 w-full" onClick={saveNote} data-testid="save-note-btn">{t("save")}</Button>
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
              {notes.slice(0, 8).map((n) => (
                <div key={n.id} className="p-3 rounded-lg bg-muted/40 border border-border text-sm flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">{n.date}</div>
                    <div className="mt-1 break-words">{n.content}</div>
                  </div>
                  <button onClick={() => deleteNote(n.id)} className="text-destructive hover:opacity-70" aria-label={t("delete")}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
